import { MERGE_BATCH_MAX_HASHES } from "@ckgo2/shared";
import { chunk } from "@ckgo2/shared/utils";

/**
 * Model dávek dialogu sloučení duplicit (F7.13 „po dávkách, ne celou knihovnou jedním
 * požadavkem“): bez Reactu, `api` i `window`, aby šel testovat v projektu `admin` (prostředí
 * `node`). Dialog z náhledu vezme `hashes` a pošle je těmito dávkami za sebou.
 */

/** Dávky v pořadí vstupu, bez opakování otisku, nejvýš `MERGE_BATCH_MAX_HASHES` v dávce. */
export const mergeBatches = (hashes: readonly string[]): string[][] =>
  chunk([...new Set(hashes)], MERGE_BATCH_MAX_HASHES);

/** Stav běhu dávek; `done` = počet otisků z dávek, na které přišla úspěšná odpověď. */
export type MergeRunState =
  | { phase: "idle" }
  | { phase: "running"; done: number; total: number }
  | { phase: "done"; done: number; total: number }
  | { phase: "error"; done: number; total: number; message: string };

/**
 * Pošle dávky za sebou (další až po odpovědi předchozí); po chybě další nepošle.
 * `onProgress` dostane `running` s `done: 0` před první dávkou a po každé úspěšné dávce,
 * nakonec `done` nebo `error`. `isCancelled()` se ptá před každou dávkou: zrušený běh
 * (zavřený dialog) vrátí poslední stav bez dalšího požadavku a bez hlášení.
 */
export async function runMergeBatches(opts: {
  hashes: readonly string[];
  post: (batch: string[]) => Promise<unknown>;
  onProgress: (state: MergeRunState) => void;
  isCancelled?: () => boolean;
}): Promise<MergeRunState> {
  const batches = mergeBatches(opts.hashes);
  const total = batches.reduce((sum, b) => sum + b.length, 0);
  let done = 0;
  const report = (state: MergeRunState) => {
    opts.onProgress(state);
    return state;
  };
  let last = report({ phase: "running", done, total });
  for (const batch of batches) {
    if (opts.isCancelled?.()) return last;
    try {
      await opts.post(batch);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      return report({ phase: "error", done, total, message });
    }
    done += batch.length;
    last = report({ phase: "running", done, total });
  }
  return report({ phase: "done", done, total });
}
